// 缩略图轨道（任务 3.4）· HTML5 拖拽排序 + 上移/下移/删页 + 新增页
// 缩略图直接复用 SlideCanvas 渲染（只读、禁指针），与画布/导出三方同源。

import { useState } from "react";
import SlideCanvas from "../SlideCanvas";
import type { EditorSlide } from "../../lib/slides";

interface ThumbRailProps {
  slides: EditorSlide[];
  cur: number;
  readonly?: boolean;
  onSelect: (i: number) => void;
  onMove: (from: number, to: number) => void;
  onDelete: (i: number) => void;
  onAdd: () => void;
  /** V2：deck 风格/主题（legacy 或缺省走旧渲染分支） */
  style?: string | null;
  themeId?: string | null;
}

export default function ThumbRail({ slides, cur, readonly, onSelect, onMove, onDelete, onAdd, style, themeId }: ThumbRailProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-3">
      {slides.map((s, i) => (
        <div
          key={s.id ?? `local-${i}`}
          draggable={!readonly}
          onDragStart={() => setDragIdx(i)}
          onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
          onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
          onDragLeave={() => setOverIdx((v) => (v === i ? null : v))}
          onDrop={(e) => {
            e.preventDefault();
            if (dragIdx !== null && dragIdx !== i) onMove(dragIdx, i);
            setDragIdx(null); setOverIdx(null);
          }}
          onClick={() => onSelect(i)}
          className={`group relative cursor-pointer rounded-lg ring-2 transition ${
            i === cur ? "ring-primary" : "ring-transparent hover:ring-slate-300"
          } ${overIdx === i && dragIdx !== null ? "outline outline-2 outline-dashed outline-secondary" : ""} ${
            dragIdx === i ? "opacity-40" : ""
          }`}
          title={`第 ${i + 1} 页`}
        >
          <span className="absolute left-1 top-1 z-10 rounded bg-slate-800/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
            {i + 1}
          </span>
          {!readonly && (
            <span className="absolute right-1 top-1 z-10 hidden gap-1 group-hover:flex">
              <button
                className="rounded bg-white/90 px-1 text-xs shadow hover:bg-slate-100" title="上移"
                onClick={(e) => { e.stopPropagation(); if (i > 0) onMove(i, i - 1); }}
              >↑</button>
              <button
                className="rounded bg-white/90 px-1 text-xs shadow hover:bg-slate-100" title="下移"
                onClick={(e) => { e.stopPropagation(); if (i < slides.length - 1) onMove(i, i + 1); }}
              >↓</button>
              <button
                className="rounded bg-white/90 px-1 text-xs text-red-600 shadow hover:bg-red-50" title="删除本页"
                onClick={(e) => { e.stopPropagation(); onDelete(i); }}
              >🗑</button>
            </span>
          )}
          <div className="pointer-events-none overflow-hidden rounded-lg">
            <SlideCanvas slide={s.content} pageIdx={i + 1} style={style} themeId={themeId} totalPages={slides.length} />
          </div>
        </div>
      ))}
      {!readonly && (
        <button className="btn-ghost w-full !py-1.5 text-xs" onClick={onAdd}>＋ 新增页</button>
      )}
    </div>
  );
}
